"use client";

import Link from "next/link";
import { ArrowRight, Server, Database, Brain, Shield, Zap, Cpu, Lock, Languages, Eye, ChevronRight } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell,
  CartesianGrid,
} from "recharts";
import { Container, Section } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { Reveal } from "@/components/ui/reveal";
import { Counter } from "@/components/ui/counter";
import {
  HubHero, HubOverview, HubFeatures, HubUseCases, HubWhy, HubFAQ, HubCTA,
  CategoryHubComposer,
} from "@/components/ui/category-hub";
import {
  technologyStats, technologyChildren, technologyFeatures, technologyUseCases,
  technologyWhy, technologyFaq, technologyCta, techLayers, slaMetrics,
} from "@/lib/data/technology-hub";

/* ============================================================
   BESPOKE 1 — Technology stack layered diagram.
   6 horizontal layers from Security (bottom) to Application (top).
   ============================================================ */
function TechnologyStackDiagram() {
  return (
    <div className="relative w-full max-w-[520px] aspect-square mx-auto">
      <svg viewBox="0 0 520 520" className="w-full h-full" aria-hidden>
        <defs>
          <radialGradient id="tCore" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#2563eb" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="260" cy="260" r="210" fill="url(#tCore)" />
        {/* Stack layers from bottom (layer 1) to top (layer 6) */}
        {techLayers.slice().reverse().map((layer, i) => {
          const yTop = 60 + i * 64;
          const h = 56;
          return (
            <g key={layer.layer}>
              <rect
                x="80" y={yTop} width="360" height={h}
                fill={`color-mix(in oklch, var(--color-${layer.accent}) 14%, transparent)`}
                stroke={`var(--color-${layer.accent})`}
                strokeWidth="0.7"
                rx="6"
              />
              <text
                x="92" y={yTop + 16} fontSize="6" fontWeight="800"
                fill={`var(--color-${layer.accent})`} letterSpacing="1.2"
              >
                LAYER 0{layer.layer} · {layer.label.toUpperCase()}
              </text>
              <text
                x="92" y={yTop + 30} fontSize="11" fontWeight="900"
                fill="white" letterSpacing="-0.3"
              >
                {layer.label}
              </text>
              <text
                x="92" y={yTop + 46} fontSize="5.5"
                fill="rgb(255 255 255 / 0.6)" letterSpacing="0.3"
              >
                {layer.surfaces.slice(0, 4).join(" · ")}
              </text>
            </g>
          );
        })}
        {/* Side rail markers */}
        {techLayers.map((layer, i) => {
          const yTop = 60 + (techLayers.length - 1 - i) * 64 + 28;
          return (
            <g key={`m-${layer.layer}`}>
              <line x1="55" y1={yTop} x2="80" y2={yTop} stroke={`var(--color-${layer.accent})`} strokeWidth="0.6" strokeOpacity="0.6" />
              <circle cx="55" cy={yTop} r="2" fill={`var(--color-${layer.accent})`} />
              <text x="50" y={yTop + 2.5} textAnchor="end" fontSize="6" fontWeight="700" fill="rgb(255 255 255 / 0.4)">
                L0{layer.layer}
              </text>
            </g>
          );
        })}
        {/* Up arrow on the right indicating "stacks up" */}
        <line x1="455" y1="60" x2="455" y2="450" stroke="rgb(255 255 255 / 0.15)" strokeWidth="0.6" strokeDasharray="2 4" />
        <text x="455" y="40" textAnchor="middle" fontSize="6" fontWeight="700" fill="rgb(255 255 255 / 0.5)" letterSpacing="1">
          ↑ STACKS UP ↑
        </text>
        <text x="455" y="465" textAnchor="middle" fontSize="6" fontWeight="700" fill="rgb(255 255 255 / 0.5)" letterSpacing="1">
          ↓ FOUNDATION ↓
        </text>
      </svg>
      <div className="absolute top-2 left-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "0.5s" }}>
        <Cpu className="h-3.5 w-3.5" style={{ color: "var(--color-denim)" }} />
        <span className="text-xs font-semibold">6 Stack Layers</span>
      </div>
      <div className="absolute top-2 right-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "1.5s" }}>
        <Shield className="h-3.5 w-3.5" style={{ color: "var(--color-emerald)" }} />
        <span className="text-xs font-semibold">Zero Trust</span>
      </div>
      <div className="absolute bottom-2 left-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "2.5s" }}>
        <Database className="h-3.5 w-3.5" style={{ color: "var(--color-cyan)" }} />
        <span className="text-xs font-semibold">1PB+ Fabric</span>
      </div>
      <div className="absolute bottom-2 right-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "3.5s" }}>
        <Zap className="h-3.5 w-3.5" style={{ color: "var(--color-crimson)" }} />
        <span className="text-xs font-semibold">&lt;1s Stream</span>
      </div>
    </div>
  );
}

/* ============================================================
   BESPOKE 2 — SLA bar chart.
   Performance metrics against SLA targets.
   ============================================================ */
function SlaBarChart() {
  const data = slaMetrics.map((s) => ({
    name: s.metric,
    value: s.value,
    target: s.target,
    accent: s.accent,
    unit: s.unit,
  }));
  return (
    <div className="space-y-3">
      {data.map((d, i) => {
        // Compute a "fill" width (visual indicator of value vs arbitrary ceiling)
        const maxByMetric: Record<string, number> = {
          "Stream Latency": 2,
          "API Response (p99)": 300,
          "Dashboard Refresh": 2,
          "Model Inference": 80,
          "Failover Time": 2,
          "Backup Window": 60,
        };
        const max = maxByMetric[d.name] ?? 100;
        const pct = Math.min((d.value / max) * 100, 100);
        return (
          <Reveal key={d.name} delay={i * 60}>
            <div className="glass rounded-xl p-4" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${d.accent}) 12%, transparent)` }}>
              <div className="flex items-center justify-between mb-2 gap-3">
                <span className="text-sm font-semibold flex-1">{d.name}</span>
                <span className="eyebrow-tiny font-bold" style={{ color: `var(--color-${d.accent})` }}>
                  TARGET: {d.target}
                </span>
                <span className="text-sm font-bold font-mono-num w-20 text-right" style={{ color: `var(--color-${d.accent})` }}>
                  {d.value}{d.unit}
                </span>
              </div>
              <div className="h-2 rounded-full bg-[rgb(var(--border)/0.15)] overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${pct}%`,
                    background: `linear-gradient(90deg, var(--color-${d.accent}), color-mix(in oklch, var(--color-${d.accent}) 50%, transparent))`,
                  }}
                />
              </div>
            </div>
          </Reveal>
        );
      })}
    </div>
  );
}

/* ============================================================
   BESPOKE 3 — Tech stack layer cards.
   ============================================================ */
function TechLayerCards() {
  return (
    <div className="space-y-3">
      {techLayers.map((layer, i) => (
        <Reveal key={layer.layer} delay={i * 50}>
          <div
            className="glass rounded-2xl p-5 card-glow"
            style={{ ["--glow" as string]: `var(--color-${layer.accent})` }}
          >
            <div className="grid sm:grid-cols-[120px_1fr_auto] gap-4 items-center">
              <div>
                <div
                  className="grid place-items-center h-12 w-12 rounded-xl text-sm font-bold tracking-wider"
                  style={{
                    background: `color-mix(in oklch, var(--color-${layer.accent}) 22%, transparent)`,
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${layer.accent}) 40%, transparent)`,
                    color: `var(--color-${layer.accent})`,
                  }}
                >
                  L0{layer.layer}
                </div>
              </div>
              <div>
                <h3 className="text-base font-bold">{layer.label}</h3>
                <p className="mt-1 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{layer.description}</p>
              </div>
              <div className="flex flex-wrap gap-1.5 justify-end">
                {layer.surfaces.map((s) => (
                  <span
                    key={s}
                    className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded border"
                    style={{
                      color: `var(--color-${layer.accent})`,
                      borderColor: `color-mix(in oklch, var(--color-${layer.accent}) 25%, transparent)`,
                      background: `color-mix(in oklch, var(--color-${layer.accent}) 8%, transparent)`,
                    }}
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

/* ============================================================
   PAGE COMPOSER
   ============================================================ */
export function TechnologyHubPage() {
  return (
    <CategoryHubComposer
      overviewWrap="denim"
      featuresWrap="crimson"
      useCasesWrap="emerald"
      whyWrap="purple"
      faqWrap="amber"
      ctaWrap="violet"
      hero={
        <section id="top" className="relative overflow-hidden pt-8 pb-16 sm:pt-12 sm:pb-24">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute top-10 left-1/4 h-72 w-72 rounded-full bg-[var(--color-denim)] opacity-22 blur-[130px] animate-pulse-glow" />
            <div className="absolute top-32 right-1/4 h-80 w-80 rounded-full bg-[var(--color-cyan)] opacity-18 blur-[140px] animate-pulse-glow" style={{ animationDelay: "2s" }} />
            <div className="absolute bottom-10 left-1/3 h-72 w-72 rounded-full bg-[var(--color-emerald)] opacity-15 blur-[140px] animate-pulse-glow" style={{ animationDelay: "4s" }} />
          </div>
          <Container size="wide">
            <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-6 items-center">
              <div className="flex flex-col items-start gap-6">
                <Reveal>
                  <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full glass">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--color-emerald)] opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--color-emerald)]" />
                    </span>
                    <span className="eyebrow-tiny uppercase tracking-[0.28em]">Perception X2 Technology Stack</span>
                  </div>
                </Reveal>
                <Reveal delay={80}>
                  <h1 className="display-hero text-balance">
                    Ten Technology{" "}
                    <span className="bg-gradient-to-br from-[var(--color-denim)] via-[var(--color-cyan)] to-[var(--color-emerald)] bg-clip-text text-transparent">
                      Surfaces. One Stack.
                    </span>
                  </h1>
                </Reveal>
                <Reveal delay={160}>
                  <p className="text-[1.35rem] sm:text-[1.5rem] leading-[1.45] font-medium text-[rgb(var(--fg))] max-w-2xl">
                    Proprietary AI, multi-lingual NLP, multi-modal vision, petabyte data fabric, sub-second stream processing — engineered in-house over 15+ years.
                  </p>
                </Reveal>
                <Reveal delay={220}>
                  <p className="text-body leading-relaxed text-[rgb(var(--fg-muted))] max-w-2xl">
                    From AI/ML and NLP to infrastructure and security — every surface is built to operate at machine speed, with audit-grade lineage and zero-trust security throughout. The fastest, most secure perception platform in operation.
                  </p>
                </Reveal>
                <Reveal delay={280}>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <Link
                      href="/technology/ai-ml"
                      className="btn-primary-gradient h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl shadow-[var(--color-denim)/30]"
                    >
                      Explore AI / ML <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link
                      href="/security-architecture"
                      className="btn-glass h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base"
                    >
                      Security Architecture
                    </Link>
                  </div>
                </Reveal>
                <Reveal delay={360}>
                  <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-3xl w-full">
                    {technologyStats.map((s) => (
                      <div
                        key={s.label}
                        className="glass rounded-2xl p-3 card-glow"
                        style={{ ["--glow" as string]: `var(--color-${s.accent})` }}
                      >
                        <div className="display-stat-sm" style={{ color: `var(--color-${s.accent})` }}>
                          <Counter to={s.to} suffix={s.suffix ?? ""} decimals={s.decimals ?? 0} />
                        </div>
                        <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-1.5 font-semibold uppercase tracking-wider">
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </Reveal>
              </div>
              <Reveal delay={200} className="order-first lg:order-last">
                <TechnologyStackDiagram />
              </Reveal>
            </div>
          </Container>
        </section>
      }
      overview={
        <HubOverview
          eyebrow="Ten Technology Surfaces"
          title="Every Layer of the Perception X2 Stack"
          description="The Perception X2 platform is organized into ten technology surfaces — from AI/ML to security architecture. Each is a purpose-built technology pillar with deep integration into the composed stack."
          items={technologyChildren}
          accent="denim"
        />
      }
      features={
        <>
          <Section id="layers" className="relative">
            <Container>
              <SectionHeading
                eyebrow="Stack Architecture"
                title="Six Layers, One Engineering Culture"
                description="Every Perception X2 technology surface composes into one of six architectural layers — from the security foundation at the base to the application surface at the top. The stack is engineered in-house over 15+ years."
                accent="crimson"
              />
              <TechLayerCards />
            </Container>
          </Section>
          <Section id="features" className="relative mt-8">
            <Container>
              <SectionHeading
                eyebrow="Technology Capabilities"
                title="What the Stack Delivers"
                description="Eight foundational capabilities shared across every technology surface — each engineered for machine-speed operation and defense-grade protection."
                accent="purple"
              />
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {technologyFeatures.map((f, i) => (
                  <Reveal key={f.title} delay={i * 50}>
                    <div
                      className="glass rounded-2xl p-5 h-full card-glow"
                      style={{ ["--glow" as string]: `var(--color-${f.accent})` }}
                    >
                      <div
                        className="grid place-items-center h-10 w-10 rounded-lg"
                        style={{
                          background: `color-mix(in oklch, var(--color-${f.accent}) 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${f.accent}) 35%, transparent)`,
                        }}
                      >
                        <span className="text-base font-bold" style={{ color: `var(--color-${f.accent})` }}>◆</span>
                      </div>
                      <h3 className="mt-3 text-base font-bold">{f.title}</h3>
                      <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{f.description}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </Container>
          </Section>
        </>
      }
      useCases={
        <HubUseCases
          eyebrow="Technology Applications"
          title="Six Operational Patterns"
          description="The Perception X2 technology stack serves six primary operational patterns — from multi-language engagement to sovereign-grade data residency."
          cases={technologyUseCases}
          accent="emerald"
        />
      }
      why={
        <>
          <Section id="sla" className="relative">
            <Container>
              <SectionHeading
                eyebrow="SLA Performance"
                title="Latency, Throughput, and Resilience"
                description="Measured against strict SLA targets across stream processing, API response, dashboard refresh, model inference, failover, and backup. Every metric verified by continuous load testing."
                accent="purple"
              />
              <SlaBarChart />
            </Container>
          </Section>
          <Section id="why" className="relative mt-8">
            <Container>
              <HubWhy
                eyebrow="Why This Technology Stack"
                title="The Differentiators That Compound"
                items={technologyWhy}
                accent="cyan"
              />
            </Container>
          </Section>
        </>
      }
      faq={
        <HubFAQ
          eyebrow="Technology FAQ"
          title="Frequently Asked Questions"
          items={technologyFaq}
          accent="amber"
        />
      }
      cta={
        <HubCTA
          eyebrow={technologyCta.eyebrow}
          title={technologyCta.title}
          description={technologyCta.description}
          primary={technologyCta.primary}
          secondary={technologyCta.secondary}
          trustItems={technologyCta.trustItems}
          accent="violet"
        />
      }
    />
  );
}
